// Small helpers shared by the queries screen (app.ts) and reports (reports.ts).

export const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
export const icon = (name: string, cls = "i") => `<svg class="${cls}" aria-hidden="true"><use href="#i-${name}"/></svg>`;
export const dateOf = (s: string) => new Date(`${s.replace(" ", "T")}Z`);
/** A field you can type in right now (a locked, read-only one doesn't count). */
export const isTyping = (t: EventTarget | null) =>
  (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement) && !(t as HTMLInputElement).readOnly;

export function ago(s: string) {
  const sec = (Date.now() - dateOf(s).getTime()) / 1000;
  if (sec < 60) return "now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d`;
  return dateOf(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export const toLogin = () => location.replace(`/login?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`);
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { headers: { "content-type": "application/json" }, ...init });
  if (res.status === 401) toLogin();
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json();
}

/** Uploads files as multipart "file" fields; returns the JSON reply, or null after showing the error. */
export async function upload<T>(path: string, files: File[]): Promise<T | null> {
  const form = new FormData();
  for (const f of files) form.append("file", f);
  const res = await fetch(path, { method: "POST", body: form });
  if (res.status === 401) { toLogin(); return null; }
  if (!res.ok) { toast((await res.json().catch(() => null))?.error ?? `Upload failed (${res.status})`); return null; }
  return res.json();
}

/** Clipboard text from a SQL client becomes a file: tab separated if it has tabs, and so on. */
export function pastedFile(text: string): File {
  const stamp = new Date().toISOString().slice(0, 16).replace("T", " ").replace(":", "");
  const first = text.trimStart().split("\n")[0] ?? "";
  const ext = /^\s*[[{]/.test(text) ? "json" : first.includes("\t") ? "tsv" : first.includes("|") ? "txt" : first.includes(",") ? "csv" : "txt";
  return new File([text], `pasted ${stamp}.${ext}`, { type: "text/plain" });
}

/** What's on the clipboard as files (a screenshot, or text as a pasted file), via the async clipboard API. */
export async function clipboardFiles(): Promise<File[] | null> {
  try {
    if (!navigator.clipboard) { toast("The browser only allows this on https. Press ⌘V instead."); return null; }
    if (navigator.clipboard.read) {
      for (const item of await navigator.clipboard.read()) {
        const img = item.types.find((t) => t.startsWith("image/"));
        if (img) {
          const blob = await item.getType(img);
          return [new File([blob], `screenshot ${new Date().toISOString().slice(0, 10)}.${img.split("/")[1]}`, { type: img })];
        }
      }
    }
    const text = await navigator.clipboard.readText();
    if (!text.trim()) { toast("The clipboard is empty"); return null; }
    return [pastedFile(text)];
  } catch {
    toast("Couldn't read the clipboard. Press ⌘V instead.");
    return null;
  }
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(msg: string) {
  const el = document.getElementById("toast")!;
  el.textContent = msg;
  el.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove("show"), 2000);
}

/** navigator.clipboard only exists on https and localhost; on plain http over the network, fall back to execCommand. */
export async function writeClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try { await navigator.clipboard.writeText(text); return true; } catch {}
  }
  const focused = document.activeElement as HTMLElement | null;
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.readOnly = true;
  ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
  document.body.append(ta);
  ta.select();
  let ok = false;
  try { ok = document.execCommand("copy"); } catch {}
  ta.remove();
  focused?.focus({ preventScroll: true });
  return ok;
}

/** Updates document.title and head meta tags so the tab title and client DOM stay in sync with the open item. */
export function setPageMeta(m: { title: string; ogTitle: string; description: string; keywords: string }) {
  document.title = m.title;
  const set = (sel: string, val: string) => document.head.querySelector<HTMLMetaElement>(sel)?.setAttribute("content", val);
  set('meta[name="description"]', m.description);
  set('meta[name="keywords"]', m.keywords);
  set('meta[property="og:title"]', m.ogTitle);
  set('meta[property="og:description"]', m.description);
  set('meta[name="twitter:title"]', m.ogTitle);
  set('meta[name="twitter:description"]', m.description);
}
